"use client";

/**
 * Le cadre « Vos références » d'un avis d'impôt, avec la ligne à recopier surlignée.
 *
 * C'est la réponse au seul vrai blocage de ces simulateurs : personne ne sait
 * quelle ligne regarder ni de quelle année. Écrire « il se lit sur votre avis
 * d'impôt » ne suffit pas, il faut le montrer.
 *
 * La maquette suit ce que dit l'administration (impots.gouv.fr, « Accéder à mon
 * espace », modifiée le 07/01/2026) : le revenu fiscal de référence figure en
 * première page du dernier avis, dans le cadre « Vos références », avec le numéro
 * fiscal et le numéro d'accès en ligne ; à défaut, dans le détail de
 * l'imposition, rubrique « Informations complémentaires ». Service-Public
 * (F13216, 01/01/2026) le situe en première et en troisième page.
 *
 * Les identifiants sont masqués et le montant est un exemple, étiqueté comme
 * tel : ce n'est pas un calcul, c'est un repère visuel.
 */

const L = {
    fr: {
        entete: "Avis d'impôt {avis} sur les revenus {revenus}",
        exemple: "Exemple",
        cadre: "Vos références",
        numeroFiscal: "Numéro fiscal",
        accesEnLigne: "Numéro d'accès en ligne",
        rfr: "Revenu fiscal de référence",
        masque: "masqué",
        aide: "Il se trouve en première page, dans le cadre « Vos références », à côté de votre numéro fiscal. S'il n'y figure pas, cherchez-le dans le détail de l'imposition, à la rubrique « Informations complémentaires ». Ne recopiez pas le revenu net imposable : c'est un autre chiffre.",
    },
    en: {
        entete: "{avis} tax notice on {revenus} income",
        exemple: "Example",
        cadre: "Vos références",
        numeroFiscal: "Numéro fiscal",
        accesEnLigne: "Numéro d'accès en ligne",
        rfr: "Revenu fiscal de référence",
        masque: "hidden",
        aide: "It is on the first page, in the box headed “Vos références” (your references), next to your tax number. If it is not there, look for it in the tax breakdown, under “Informations complémentaires”. Do not copy the net taxable income: that is a different figure.",
    },
};

/* Un identifiant masqué : la maquette montre où il se trouve, jamais sa valeur. */
function Masque({ libelle, t }) {
    return (
        <div className="flex justify-between items-baseline gap-3 py-1.5 text-sm text-gray-500 dark:text-lightBlue/70">
            <span>{libelle}</span>
            <span className="tracking-[0.2em] text-gray-400 dark:text-lightBlue/50" aria-label={t.masque}>
                •••• ••••
            </span>
        </div>
    );
}

export default function AvisImpotMaquette({ lang = "fr", anneeAvis, anneeRevenus, montantExemple = 31870 }) {
    const t = L[lang] || L.fr;
    const nombre = (v) => v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

    return (
        <div>
            <div className="rounded-xl border border-gray-200/80 dark:border-white/10 bg-gray-50/80 dark:bg-white/[0.02] p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="text-[11px] font-semibold text-gray-600 dark:text-grayDark-400 uppercase tracking-wider">
                        {t.entete.replace("{avis}", anneeAvis).replace("{revenus}", anneeRevenus)}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-lightBlue/50 shrink-0">
                        {t.exemple}
                    </span>
                </div>

                <div className="sm:ml-auto sm:max-w-sm rounded-lg border border-gray-300/80 dark:border-white/15 bg-white dark:bg-white/[0.03] px-3 pt-2.5 pb-2">
                    <div className="text-xs font-bold dark:text-white mb-1">{t.cadre}</div>
                    <Masque libelle={t.numeroFiscal} t={t} />
                    <Masque libelle={t.accesEnLigne} t={t} />
                    <div className="flex justify-between items-baseline gap-3 py-2 px-2 -mx-1 mt-1 rounded-md bg-amber-200/50 dark:bg-amber-400/15 border-y border-amber-300/50 dark:border-amber-400/20">
                        <span className="font-bold dark:text-white text-sm">{t.rfr}</span>
                        <span className="tabular-nums font-bold dark:text-white">{nombre(montantExemple)} €</span>
                    </div>
                </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">{t.aide}</p>
        </div>
    );
}
